<script>
import Swal from "sweetalert2";
import firebase from "firebase/app";
import "firebase/firestore";

import Portal from "../../layouts/portal";
import GoogleMapSimple from "@/components/widgets/GoogleMapSimple.vue";
import { clienteComputed, negocioComputed } from "@/state/helpers";
import { guardarNegocio, subirImagen } from "@/services/negocio";
import { enlaceMapa } from "@/services/portal";

/** Campos editables de la ficha, con su valor inicial vacío. */
const CAMPOS = {
  nombre: "",
  giro: "",
  nombreEmpresario: "",
  // "Háblame de ti" en la app: la descripción que el negocio escribe de sí
  // mismo y que se muestra en el directorio como "Quiénes somos".
  descripcion: "",
  promocion: "",
  horario: "",
  tags: "",
  contacto: "",
  enlace: "",
  instagram: "",
  facebook: "",
  direccion: "",
  coordenadas: "",
  img: "",
};

export default {
  page: { title: "Mi empresa" },
  components: { Portal, GoogleMapSimple },
  data() {
    return {
      editando: false,
      guardando: false,
      subiendoLogo: false,
      error: null,
      formulario: { ...CAMPOS },
    };
  },
  computed: {
    ...clienteComputed,
    ...negocioComputed,
    enlaceUbicacion() {
      return this.fichaNegocio ? enlaceMapa(this.fichaNegocio) : null;
    },
    /** Datos a mostrar en modo consulta. */
    filas() {
      const f = this.fichaNegocio || {};
      return [
        { etiqueta: "Quiénes somos", valor: f.descripcion },
        { etiqueta: "Giro", valor: f.giro },
        { etiqueta: "Responsable", valor: f.nombreEmpresario },
        { etiqueta: "Promoción", valor: f.promocion },
        { etiqueta: "Horario", valor: f.horario },
        { etiqueta: "Teléfono de contacto", valor: f.contacto },
        { etiqueta: "Teléfono de enlace", valor: f.enlace },
        { etiqueta: "Etiquetas", valor: f.tags },
        { etiqueta: "Dirección", valor: f.direccion },
      ];
    },
  },
  methods: {
    activarEdicion() {
      const f = this.fichaNegocio || {};
      this.formulario = Object.keys(CAMPOS).reduce((acc, clave) => {
        acc[clave] = f[clave] || "";
        return acc;
      }, {});
      this.editando = true;
      this.error = null;
    },

    cancelar() {
      this.editando = false;
      this.formulario = { ...CAMPOS };
    },

    /**
     * El mapa emite las coordenadas como `lat+lng`, que para una longitud
     * negativa produce el mismo `"lat+-lon"` que escribe la app. Se guarda tal
     * cual, sin convertir, para no romper la compatibilidad.
     */
    onCoordenadas(valor) {
      this.formulario.coordenadas = valor;
    },
    onDireccion(valor) {
      this.formulario.direccion = valor;
    },

    async onLogo(evento) {
      const archivo = evento.target.files && evento.target.files[0];
      if (!archivo) return;

      this.subiendoLogo = true;
      try {
        this.formulario.img = await subirImagen(archivo, "negocios");
      } catch (err) {
        console.error("[portal] error al subir el logo:", err);
        this.error = "No pudimos subir el logo. Inténtalo de nuevo.";
      } finally {
        this.subiendoLogo = false;
      }
    },

    async guardar() {
      if (!this.formulario.nombre.trim()) {
        this.error = "El nombre del negocio es obligatorio.";
        return;
      }
      if (this.subiendoLogo) {
        this.error = "Espera a que termine de subir el logo.";
        return;
      }

      this.guardando = true;
      this.error = null;

      // Registrar el negocio por primera vez convierte al usuario en comercio.
      const esNuevo = !this.fichaNegocio;

      try {
        const datos = {
          ...this.formulario,
          nombre: this.formulario.nombre.trim(),
          // La app lee unas pantallas `img` y otras `logo`; se escriben ambas
          // para que el negocio se vea igual en todas.
          logo: this.formulario.img,
        };

        await guardarNegocio(datos, this.fichaNegocio ? this.fichaNegocio.id : null);

        // Registrar el negocio también completa el perfil y activa al usuario
        // como comercio, para que aparezcan sus secciones (eventos, vacantes).
        if (esNuevo && this.perfilCliente && this.perfilCliente.id) {
          await firebase.firestore().collection("app_usuarios")
            .doc(this.perfilCliente.id)
            .update({ type: "comercio", status: "signup_completed" });
          await this.$store.dispatch("cliente/cargar", { force: true });
        }

        await this.$store.dispatch("negocio/cargar", { force: true });

        this.editando = false;
        await Swal.fire(
          esNuevo ? "¡Negocio registrado!" : "Guardado",
          esNuevo
            ? "Tu negocio ya aparece en el directorio y puedes publicar eventos y vacantes."
            : "Los datos de tu negocio se actualizaron.",
          "success"
        );

        // Registrar el negocio completa el perfil: se entra al portal por Home.
        if (esNuevo) this.$router.push({ name: "portal" });
      } catch (err) {
        console.error("[portal] error al guardar el negocio:", err);
        this.error = "No pudimos guardar los cambios. Inténtalo de nuevo.";
      } finally {
        this.guardando = false;
      }
    },
  },
  mounted() {
    this.$store.dispatch("negocio/cargar");
  },
};
</script>

<template>
  <Portal>
    <h1 class="h3 mb-1">Mi empresa</h1>
    <p class="text-muted">Los datos que ven los miembros de Recnet en el directorio.</p>

    <!-- Sin muro de comercio: aquí es donde un usuario normal registra su
         negocio y se convierte en comercio, así que debe ser accesible para
         cualquier cliente. -->
    <div>
      <p v-if="cargandoNegocio" class="text-muted">Cargando tu negocio…</p>

      <!-- Comercio sin ficha: se ofrece crearla, como hace la app. -->
      <div v-else-if="!tieneNegocio && !editando" class="empresa-card text-center">
        <i class="mdi mdi-store-plus empresa-icono"></i>
        <h2 class="h5 mt-3">Todavía no tienes tu negocio registrado</h2>
        <p class="text-muted">
          Regístralo para aparecer en el directorio de Recnet y poder publicar eventos y vacantes.
        </p>
        <b-button variant="primary" @click="activarEdicion">Registrar mi negocio</b-button>
      </div>

      <template v-else>
        <b-alert :show="!!error" variant="danger">{{ error }}</b-alert>

        <!-- Consulta -->
        <div v-if="!editando" class="empresa-card">
          <div class="d-flex align-items-center flex-wrap mb-4">
            <div class="empresa-logo mr-3">
              <img v-if="fichaNegocio.img" :src="fichaNegocio.img" :alt="fichaNegocio.nombre" />
              <i v-else class="mdi mdi-storefront"></i>
            </div>
            <div class="flex-grow-1">
              <h2 class="h4 mb-0">{{ fichaNegocio.nombre }}</h2>
              <p class="text-muted mb-0">{{ fichaNegocio.giro || 'Sin giro' }}</p>
            </div>
            <b-button variant="primary" @click="activarEdicion">Editar</b-button>
          </div>

          <ul class="list-unstyled mb-0">
            <li v-for="fila in filas" :key="fila.etiqueta" class="empresa-fila">
              <span class="empresa-etiqueta">{{ fila.etiqueta }}</span>
              <span>{{ fila.valor || '—' }}</span>
            </li>
          </ul>

          <div class="mt-3">
            <a v-if="enlaceUbicacion" :href="enlaceUbicacion" target="_blank" rel="noopener noreferrer"
              class="btn btn-outline-secondary btn-sm mr-2">
              <i class="mdi mdi-map-marker"></i> Ver ubicación
            </a>
            <a v-if="fichaNegocio.instagram" :href="fichaNegocio.instagram" target="_blank"
              rel="noopener noreferrer" class="btn btn-outline-primary btn-sm mr-2">
              <i class="mdi mdi-instagram"></i>
            </a>
            <a v-if="fichaNegocio.facebook" :href="fichaNegocio.facebook" target="_blank"
              rel="noopener noreferrer" class="btn btn-outline-primary btn-sm">
              <i class="mdi mdi-facebook"></i>
            </a>
          </div>
        </div>

        <!-- Edición -->
        <div v-else class="empresa-card">
          <h2 class="h5 mb-4">{{ tieneNegocio ? 'Editar mi negocio' : 'Registrar mi negocio' }}</h2>

          <b-row>
            <b-col md="6">
              <b-form-group label="Nombre del negocio *">
                <b-form-input v-model="formulario.nombre" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Giro">
                <b-form-input v-model="formulario.giro" placeholder="Cafetería, refaccionaria…" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Responsable">
                <b-form-input v-model="formulario.nombreEmpresario" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Horario">
                <b-form-input v-model="formulario.horario" placeholder="L-V 9AM a 6PM" />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Quiénes somos"
                description="Cuéntales a tus clientes quiénes son y a qué se dedican.">
                <b-form-textarea v-model="formulario.descripcion" rows="3"
                  placeholder="Somos un negocio dedicado a…" />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Promoción para miembros">
                <b-form-input v-model="formulario.promocion" placeholder="10% en compra mínima de $100" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Teléfono de contacto">
                <b-form-input v-model="formulario.contacto" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Teléfono de enlace">
                <b-form-input v-model="formulario.enlace" />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Etiquetas" description="Separadas por comas: ayudan a que te encuentren.">
                <b-form-input v-model="formulario.tags" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Instagram">
                <b-form-input v-model="formulario.instagram" placeholder="https://instagram.com/tunegocio" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Facebook">
                <b-form-input v-model="formulario.facebook" placeholder="https://facebook.com/tunegocio" />
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Logo">
            <div class="d-flex align-items-center">
              <div class="empresa-logo mr-3">
                <img v-if="formulario.img" :src="formulario.img" alt="Logo" />
                <i v-else class="mdi mdi-image-outline"></i>
              </div>
              <div>
                <input type="file" accept="image/*" @change="onLogo" />
                <p v-if="subiendoLogo" class="text-muted small mb-0 mt-1">Subiendo…</p>
              </div>
            </div>
          </b-form-group>

          <b-form-group label="Dirección"
            description="Búscala y confírmala en el mapa: de ahí salen las indicaciones para llegar.">
            <GoogleMapSimple :value="formulario.coordenadas" :address-value="formulario.direccion"
              @input="onCoordenadas" @update:addressValue="onDireccion" />
          </b-form-group>

          <div class="d-flex justify-content-end mt-4">
            <b-button variant="secondary" class="mr-2" @click="cancelar">Cancelar</b-button>
            <b-button variant="primary" :disabled="guardando || subiendoLogo" @click="guardar">
              {{ guardando ? 'Guardando…' : 'Guardar' }}
            </b-button>
          </div>
        </div>
      </template>
    </div>
  </Portal>
</template>

<style scoped>
.empresa-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
}

.empresa-icono {
  font-size: 56px;
  color: #adb5bd;
}

.empresa-logo {
  width: 72px;
  height: 72px;
  border-radius: 10px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #4b2e83;
  font-size: 28px;
  overflow: hidden;
}

.empresa-logo img {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.empresa-fila {
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #f1f1f4;
}

.empresa-fila:last-child {
  border-bottom: 0;
}

.empresa-etiqueta {
  font-weight: 600;
  width: 200px;
  flex-shrink: 0;
  color: #495057;
}

@media (max-width: 575.98px) {
  .empresa-fila {
    flex-direction: column;
  }

  .empresa-etiqueta {
    width: auto;
  }
}
</style>
